import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Instagram, Facebook, Youtube, ArrowRight, Music2 } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";

/** Defaults — used when the admin hasn't customised the footer yet. */
export const FOOTER_DEFAULTS = {
  tagline_lines: ["ORDINARY THINGS.", "EXTRAORDINARY", "LESSONS."],
  blurb: "Simple Truths Club is a growing collection of meaningful art created to inspire, remind, and connect us all.",
  columns: [
    {
      heading: "Shop",
      links: [
        { label: "All Artworks", url: "/shop" },
        { label: "New Releases", url: "/shop?filter=featured" },
        { label: "Best Sellers", url: "/shop?filter=featured" },
        { label: "Digital Only", url: "/shop?filter=digital" },
        { label: "Canvas Prints", url: "/shop?filter=canvas" },
        { label: "What Do You See?", url: "/what-do-you-see" },
      ],
    },
    {
      heading: "Club",
      links: [
        { label: "Subscriptions", url: "/subscriptions" },
        { label: "Founding Wall", url: "/founding-wall" },
        { label: "Member Benefits", url: "/p/member-benefits" },
        { label: "Refer & Earn", url: "/refer" },
        { label: "FAQs", url: "/faq" },
        { label: "Gift Cards", url: "/p/gift-cards" },
      ],
    },
    {
      heading: "Company",
      links: [
        { label: "About Mickey", url: "/about" },
        { label: "Support the Mission", url: "/support" },
        { label: "Our Story", url: "/p/our-story" },
        { label: "Art in the World", url: "/mobile-gallery" },
        { label: "Become a Host", url: "/partner-apply" },
        { label: "Contact", url: "/contact" },
      ],
    },
  ],
  newsletter_heading: "Stay Connected",
  newsletter_blurb: "Get stories, updates, and new truths delivered to you.",
  socials: { instagram: "", facebook: "", tiktok: "", youtube: "" },
  legal: [
    { label: "Privacy Policy", url: "/p/privacy" },
    { label: "Terms of Service", url: "/p/terms" },
    { label: "Refund Policy", url: "/p/refund" },
  ],
  copyright: "Simple Truths Club. All Rights Reserved.",
};

const isExternal = (url) => /^https?:\/\//i.test(url || "");

const NavLink = ({ url, children }) =>
  isExternal(url) ? (
    <a href={url} target="_blank" rel="noreferrer" className="hover:text-[#C9A055]">{children}</a>
  ) : (
    <Link to={url || "#"} className="hover:text-[#C9A055]">{children}</Link>
  );

/** Links that should ALWAYS appear in the footer, regardless of admin overrides.
 *  If a URL is missing across the entire admin-customized footer, we inject it
 *  into the named column (or append a new column if that heading doesn't exist). */
const MUST_HAVE_LINKS = [
  { label: "Support the Mission", url: "/support",        column: "Company" },
  { label: "Founding Wall",       url: "/founding-wall",  column: "Club" },
  { label: "Truth Wall",          url: "/truth-wall",     column: "Club" },
  { label: "Truth Markers",       url: "/truth-markers",  column: "Club" },
  { label: "Marker Shop",         url: "/marker-shop",    column: "Shop" },
  { label: "QR Gallery",          url: "/mobile-gallery", column: "Shop" },
];

function withMustHaveLinks(cfg) {
  const columns = (cfg.columns || []).map((c) => ({
    ...c,
    links: Array.isArray(c.links) ? [...c.links] : [],
  }));
  
  MUST_HAVE_LINKS.forEach((must) => {
    // Find the target column
    let col = columns.find((c) => (c.heading || "").toLowerCase() === must.column.toLowerCase());
    if (!col) {
      col = { heading: must.column, links: [] };
      columns.push(col);
    }
    
    // Check if this specific URL already exists in THIS column
    const urlExistsInColumn = col.links.some((l) => l?.url === must.url);
    if (urlExistsInColumn) return;
    
    // Add the link to the column
    col.links.push({ label: must.label, url: must.url });
  });
  
  return { ...cfg, columns };
}

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState(() => withMustHaveLinks(FOOTER_DEFAULTS));
  const [hideArt, setHideArt] = useState(false);

  const HIDDEN_ART_URLS = ["/shop", "/reflection-library", "/subscriptions", "/registry", "/truth-stand", "/what-do-you-see", "/cart", "/checkout", "/gift-cards", "/marker-shop", "/mobile-gallery"];

  useEffect(() => {
    http.get("/public/content").then(({ data }) => {
      if (data?.footer && typeof data.footer === "object") {
        // Shallow-merge so partial admin edits don't blow away unedited keys,
        // then guarantee the must-have links survive any admin customization.
        setCfg(withMustHaveLinks({ ...FOOTER_DEFAULTS, ...data.footer }));
      }
    }).catch(() => {});
    http.get("/public/settings").then(({ data }) => setHideArt(!!data?.hide_art_collection)).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try { await http.post("/newsletter", { email }); toast.success("You're in. Watch your inbox."); setEmail(""); }
    catch { toast.error("Could not subscribe."); }
    finally { setLoading(false); }
  };

  const socials = cfg.socials || {};
  const SOCIAL_ICONS = [
    { key: "instagram", url: socials.instagram, Icon: Instagram, label: "Instagram" },
    { key: "facebook", url: socials.facebook, Icon: Facebook, label: "Facebook" },
    { key: "tiktok", url: socials.tiktok, Icon: Music2, label: "TikTok" },
    { key: "youtube", url: socials.youtube, Icon: Youtube, label: "YouTube" },
  ].filter(s => s.url);

  return (
    <footer data-testid="site-footer" className="bg-[#050505] border-t border-white/10 mt-16">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-10 py-14 md:py-16">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="lg:col-span-3">
            <img
              src="/stc-seal.png"
              alt="Simple Truths Club"
              data-testid="footer-stc-emblem"
              className="w-32 h-32 md:w-36 md:h-36 object-contain mb-5"
            />
            <h3 className="font-display text-[#F4F1EA] text-xl md:text-2xl tracking-wide leading-tight mb-3" data-testid="footer-tagline">
              {(cfg.tagline_lines || []).map((l, i) => (
                <span key={i}>{l}{i < (cfg.tagline_lines.length - 1) ? <br/> : null}</span>
              ))}
            </h3>
            <p className="font-body text-[13px] text-[#8a857d] leading-relaxed">{cfg.blurb}</p>
          </div>

          {/* Editable columns */}
          {(cfg.columns || []).map((col, i) => {
            const links = hideArt
              ? (col.links || []).filter(l => !HIDDEN_ART_URLS.some(u => l.url === u || l.url?.startsWith(u + "?")))
              : (col.links || []);
            if (links.length === 0) return null;
            return (
              <div key={i} className="lg:col-span-2" data-testid={`footer-col-${i}`}>
                <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] mb-4">{col.heading}</div>
                <ul className="space-y-2.5 font-body text-[13px] text-[#B5B0A7]">
                  {links.map((l, j) => (
                    <li key={j}><NavLink url={l.url}>{l.label}</NavLink></li>
                  ))}
                </ul>
              </div>
            );
          })}

          {/* CONNECT */}
          <div className="lg:col-span-3">
            <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] mb-4">{cfg.newsletter_heading}</div>
            <p className="font-body text-[13px] text-[#B5B0A7] mb-4 leading-relaxed">{cfg.newsletter_blurb}</p>
            <form onSubmit={subscribe} className="flex mb-5" data-testid="footer-newsletter">
              <input value={email} onChange={e => setEmail(e.target.value)} type="email" required placeholder="Enter your email" data-testid="newsletter-email-input" className="stc-input flex-1 rounded-r-none"/>
              <button type="submit" disabled={loading} data-testid="newsletter-submit" className="btn-gold rounded-l-none px-5"><ArrowRight size={14}/></button>
            </form>
            {SOCIAL_ICONS.length > 0 && (
              <div className="flex gap-3" data-testid="footer-socials">
                {SOCIAL_ICONS.map(({ key, url, Icon, label }) => (
                  <a key={key} href={url} target="_blank" rel="noreferrer" data-testid={`footer-social-${key}`} className="w-9 h-9 border border-white/15 hover:border-[#C9A055] hover:text-[#C9A055] flex items-center justify-center text-[#F4F1EA] transition-colors" aria-label={label}>
                    <Icon size={14}/>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="font-ui-tight text-[11px] text-[#6a6660]">© {new Date().getFullYear()} {cfg.copyright}</div>
          <div className="flex items-center gap-6 font-ui-tight text-[11px] text-[#6a6660]" data-testid="footer-legal">
            {(cfg.legal || []).map((l, i) => (
              <NavLink key={i} url={l.url}>{l.label}</NavLink>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
